import React, { useEffect, useState } from 'react'
import { saveFile, isNativeApp, isTouchDevice } from '../../../../../_utils/fileExport'
import { hapticSuccess, hapticTick } from '../../../../../_utils/haptics'
import { exportPost } from '../render/render'
import { captionText } from '../data/captions'
import { markPosted, markSourceUsed } from '../data/store'
import { slidesFor, isStock } from '../data/postTypes'
import { checkTimes } from '../data/freeSlots'
import { isPast, longDate, postingDay } from '../data/dates'
import { CloseIcon, CopyIcon, DownloadIcon, CheckIcon, InstagramIcon, TikTokIcon } from './icons'

// Posting is two taps: save the pictures, copy the words, then paste in
// Instagram or TikTok. (Posting for the salon needs Meta and TikTok to
// approve Beu first; see docs/SOCIAL_BACKEND.md.)
const slug = (s) => String(s || 'post').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'post'

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch (x) {
      return false
    }
  }
}

const ShareSheet = ({ post, theme, onClose, onPosted, onBeforeShare, onChange, onEdit }) => {
  const [state, setState] = useState('working')
  const [blobs, setBlobs] = useState([])
  const [urls, setUrls] = useState([])
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [posted, setPosted] = useState(post.status === 'posted')
  const [gone, setGone] = useState([])
  const format = post.format || 'post'
  const count = slidesFor(post, format).length
  const c = post.content || {}

  // A before and after is two of the salon's own photos, or nothing
  const pair = post.type === 'beforeAfter' ? [post.photos && post.photos.before, post.photos && post.photos.after] : null
  const pairMissing = !!pair && pair.some(ph => !ph || !ph.src || isStock(ph.src))
  const leadStock = !pair && Object.values(post.photos || {}).some(ph => ph && isStock(ph.src))
  const dated = (post.type === 'freeSlots' || post.type === 'oneSlot') && c.date
  const past = dated && isPast(c.date, postingDay(post))
  const times = post.type === 'freeSlots' ? (c.times || []).filter(Boolean) : [c.time].filter(Boolean)

  // The pictures, made again whenever the post changes here
  const renderKey = JSON.stringify([post.type, post.family, post.layout, post.colourway, post.photoColour, post.tune, theme && theme.key, format, c, post.photos, post.plannedAt])
  useEffect(() => {
    if (pairMissing) return undefined
    let alive = true
    let made = []
    setState('working')
    exportPost(post, theme, format)
      .then((list) => {
        if (!alive) return
        made = list.map(b => URL.createObjectURL(b))
        setBlobs(list)
        setUrls(made)
        setState('ready')
      })
      .catch(() => { if (alive) setState('error') })
    return () => { alive = false; made.forEach(u => URL.revokeObjectURL(u)) }
  }, [renderKey, pairMissing]) // eslint-disable-line react-hooks/exhaustive-deps

  // Free times posted a while after they were found may have been booked
  useEffect(() => {
    if (!dated || past || !times.length) return undefined
    let alive = true
    // One stylist's post is checked against their diary, not anyone's
    checkTimes(c.date, times, c.staffId || null).then((res) => { if (alive && res) setGone(res.gone) })
    return () => { alive = false }
  }, [dated, c.date, c.staffId, times.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps
  const dropGone = () => {
    onChange({ content: { ...c, times: times.filter(t => !gone.includes(t)) }, touched: { ...(post.touched || {}), times: true } })
    setGone([])
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const name = slug(post.name || (post.caption && post.caption.title))
  const files = () => blobs.map((b, i) => new File([b], `${name}${blobs.length > 1 ? `-${i + 1}` : ''}.jpg`, { type: 'image/jpeg' }))

  const saveImages = async () => {
    if (state !== 'ready') return
    onBeforeShare()
    // Phones: one share sheet with every picture, so they can go straight
    // into Instagram or TikTok, or into Photos
    if (!isNativeApp() && isTouchDevice() && navigator.canShare) {
      const list = files()
      if (navigator.canShare({ files: list })) {
        try {
          await navigator.share({ files: list })
          setSaved(true)
          hapticSuccess()
          return
        } catch (e) {
          if (e && e.name === 'AbortError') return
        }
      }
    }
    for (let i = 0; i < blobs.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const how = await saveFile({ blob: blobs[i], filename: files()[i].name, title: post.caption && post.caption.title })
      if (how === 'cancelled') return
    }
    setSaved(true)
    hapticSuccess()
  }

  const copyCaption = async () => {
    const ok = await copyText(captionText(post.caption))
    if (ok) { setCopied(true); hapticTick(); setTimeout(() => setCopied(false), 2400) }
  }

  const open = (url) => { window.open(url, '_blank', 'noopener') }

  const markDone = () => {
    const current = onBeforeShare()
    const next = markPosted(current.id)
    if (post.source) markSourceUsed(post.source)
    setPosted(true)
    hapticSuccess()
    if (next) onPosted(next)
    setTimeout(onClose, 700)
  }

  return (
    <div className="sx-sheet-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sx-sheet sx-share" role="dialog" aria-modal="true" aria-label="Share your post">
        <div className="sx-sheet-head">
          <h2 className="sx-sheet-title">Share your post</h2>
          <button type="button" className="sx-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>

        {pairMissing && (
          <div className="sx-notice">
            <p><strong>Add both photos first.</strong> A before and after needs two photos of the same client, taken from a similar angle. Stock photos can’t be used for a real result.</p>
            <button type="button" className="sx-btn" onClick={() => onEdit('photos')}>Add photos</button>
          </div>
        )}
        {past && (
          <div className="sx-notice">
            <p><strong>This day has passed.</strong> The post is for {longDate(c.date)}. Pick a new day before sharing.</p>
            <button type="button" className="sx-btn" onClick={() => onEdit('words')}>Change the day</button>
          </div>
        )}
        {gone.length > 0 && (
          <div className="sx-notice">
            <p><strong>{gone.join(' and ')} {gone.length === 1 ? 'has' : 'have'} been booked</strong> since this post was made.</p>
            {post.type === 'freeSlots' && gone.length < times.length
              ? <button type="button" className="sx-btn" onClick={dropGone}>Take {gone.length === 1 ? 'it' : 'them'} off</button>
              : <button type="button" className="sx-btn" onClick={() => onEdit('words')}>Change the time</button>}
          </div>
        )}
        {leadStock && <p className="sx-hint sx-share-note">This post uses a stock photo. Your own work gets more bookings.</p>}
        {!pairMissing && (
        <>
        <div className={`sx-share-previews${format === 'story' ? ' is-story' : ''}`}>
          {state === 'working' && Array.from({ length: count }).map((_, i) => <span key={i} className="sx-share-ph" />)}
          {state === 'ready' && urls.map((u, i) => <img key={u} src={u} alt={`Slide ${i + 1}`} />)}
          {state === 'error' && <p className="sx-error">One of the photos can’t be saved from here. Swap it for one from your uploads and try again.</p>}
        </div>

        <ol className="sx-steps">
          <li className={saved ? 'is-done' : ''}>
            <span className="sx-step-n">{saved ? <CheckIcon size={14} /> : 1}</span>
            <div className="sx-step-body">
              <strong>Save the {count > 1 ? `${count} pictures` : 'picture'}</strong>
              {count > 1 && <span>Pick them in this order when you post.</span>}
            </div>
            <button type="button" className="sx-btn" disabled={state !== 'ready'} onClick={saveImages}><DownloadIcon size={16} />{saved ? 'Save again' : 'Save'}</button>
          </li>
          <li className={copied ? 'is-done' : ''}>
            <span className="sx-step-n">{copied ? <CheckIcon size={14} /> : 2}</span>
            <div className="sx-step-body">
              <strong>Copy the caption</strong>
              <span className="sx-step-caption">{(post.caption && post.caption.text) || ''}</span>
            </div>
            <button type="button" className="sx-btn" onClick={copyCaption}><CopyIcon size={16} />{copied ? 'Copied' : 'Copy'}</button>
          </li>
          <li>
            <span className="sx-step-n">3</span>
            <div className="sx-step-body">
              <strong>Post it</strong>
              <span>Open the app, add the pictures and paste the caption.</span>
            </div>
            <div className="sx-step-apps">
              <button type="button" className="sx-btn sx-btn--icon" onClick={() => open('https://www.instagram.com/')} aria-label="Open Instagram"><InstagramIcon /></button>
              <button type="button" className="sx-btn sx-btn--icon" onClick={() => open('https://www.tiktok.com/upload')} aria-label="Open TikTok"><TikTokIcon /></button>
            </div>
          </li>
        </ol>

        </>
        )}

        <div className="sx-sheet-actions">
          <button type="button" className="sx-btn-ghost" onClick={onClose}>Not yet</button>
          <button type="button" className="sx-btn-primary" onClick={markDone} disabled={posted || pairMissing}>{posted ? 'Posted' : 'I’ve posted it'}</button>
        </div>
      </div>
    </div>
  )
}

export default ShareSheet
